/**
 * MobilePracticeShell: the phone practice UX (layout). Behaviour lives in
 * usePracticeShell so the desktop shell can share it. MusicXMLPlayer picks
 * this shell on phones (utils/phoneDevice) and DesktopPracticeShell elsewhere.
 *
 * Chrome states (screen space is the scarce resource):
 *   SETUP    - stopped: top bar + full dock in flow (portrait).
 *   PRACTICE - playing or paused: no top bar, a single strip.
 *   Expanded - the dock slides up over the score from the strip's tab;
 *              folds away on the next play/pause or after 5s idle while playing.
 *   Landscape always uses the strip; the dock is only ever the slide-up sheet.
 */
import React, { useEffect, useRef, useState } from 'react';
import MobileDock from './MobileDock';
import MobileStrip from './MobileStrip';
import MobileMoreSheet from './MobileMoreSheet';
import MobileSongSheet from './MobileSongSheet';
import SelectMePartDialog from '../SelectMePartDialog';
import SettingsControls from '../SettingsControls';
import LyricsOverlay from './LyricsOverlay';
import { ChevronDownIcon } from './icons';
import { usePracticeShell } from './usePracticeShell';
import strings from './strings';
import '../../styles/mobile-practice.css';

const EXPANDED_IDLE_MS = 5000;
const SWIPE_DOWN_PX = 24;

// While the shell is up the page must not zoom: iOS Safari auto-zooms when a
// small input is focused and lets a pinch zoom the whole page, after which
// position:fixed panels pan with the finger. maximum-scale=1 stops the focus
// zoom, the gesture handlers stop pinch, and viewport-fit=cover enables the
// safe-area insets. All applied at runtime so index.html stays untouched.
const SHELL_VIEWPORT = 'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover';
function useViewportLock(active) {
  useEffect(() => {
    if (!active) return undefined;
    const meta = document.querySelector('meta[name="viewport"]');
    const original = meta ? meta.getAttribute('content') : null;
    if (meta) meta.setAttribute('content', SHELL_VIEWPORT);
    const block = (e) => e.preventDefault();
    document.addEventListener('gesturestart', block, { passive: false });
    document.addEventListener('gesturechange', block, { passive: false });
    return () => {
      if (meta && original !== null) meta.setAttribute('content', original);
      document.removeEventListener('gesturestart', block);
      document.removeEventListener('gesturechange', block);
    };
  }, [active]);
}

// iOS keeps a page zoom (a pinch, or its auto-zoom into a text box under
// 16px) until the user pinches back out, which the shell blocks, and Safari
// ignores maximum-scale for it. A zoomed shell hangs off the right and bottom
// of the screen, so while zoomed it is pinned to the visible area instead.
const ZOOMED_SCALE = 1.01;
function useFitZoomedViewport(active, shellRef) {
  useEffect(() => {
    const vv = window.visualViewport;
    const el = shellRef.current;
    if (!active || !vv || !el) return undefined;
    let width = el.offsetWidth;
    const fit = () => {
      const zoomed = vv.scale > ZOOMED_SCALE;
      Object.assign(el.style, zoomed ? {
        left: `${vv.offsetLeft}px`, top: `${vv.offsetTop}px`, right: 'auto', bottom: 'auto',
        width: `${vv.width}px`, height: `${vv.height}px`
      } : { left: '', top: '', right: '', bottom: '', width: '', height: '' });
      // OSMD re-lays the score on window resize only, which a zoom never fires
      if (Math.abs(el.offsetWidth - width) > 1) {
        width = el.offsetWidth;
        window.dispatchEvent(new Event('resize'));
      }
    };
    fit();
    vv.addEventListener('resize', fit);
    vv.addEventListener('scroll', fit);
    return () => {
      vv.removeEventListener('resize', fit);
      vv.removeEventListener('scroll', fit);
      Object.assign(el.style, { left: '', top: '', right: '', bottom: '', width: '', height: '' });
    };
  }, [active, shellRef]);
}

function useLandscape() {
  const query = '(orientation: landscape)';
  const [landscape, setLandscape] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setLandscape(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return landscape;
}

const MobilePracticeShell = ({ active, controls, children }) => {
  const {
    title, onClose, isLoading, onSongSelect, onLocalFileLoad, onRequireSignIn,
    isPlaying, partControls,
    playerState, playerActions, setStatus, getCurrentFileContent, loadMusicXMLFromContent,
    hasLoadedFile, currentPresetName
  } = controls;

  const shell = usePracticeShell({ active, controls, ui: 'mobile_shell' });
  const { inPractice, beat, hasMe, mePending, mePartName, openMePart, closeMePart, selectMePart,
    meDialogVisible, moreOpen, setMoreOpen, openMore, songsOpen, setSongsOpen,
    blend, transport, show, zoomControl, section, tempo, partsOverride, setPartsOverride,
    stepHints, setStepHints, lyrics, magnify, hasSection } = shell;

  useViewportLock(active);
  const shellRef = useRef(null);
  useFitZoomedViewport(active, shellRef);
  const landscape = useLandscape();

  // ----- chrome state -----
  const [expanded, setExpanded] = useState(false);
  const [interactionTick, setInteractionTick] = useState(0);

  // A play/pause tap, or leaving the song, always folds the sheet away
  useEffect(() => { setExpanded(false); }, [isPlaying, active]);

  // While playing, an expanded sheet folds away after a few idle seconds
  useEffect(() => {
    if (!(expanded && isPlaying)) return undefined;
    const timer = setTimeout(() => setExpanded(false), EXPANDED_IDLE_MS);
    return () => clearTimeout(timer);
  }, [expanded, isPlaying, interactionTick]);

  const dockInline = !landscape && !inPractice;   // portrait SETUP
  const topbarOverlay = landscape && !inPractice; // landscape SETUP
  const showStrip = !dockInline;
  const showSheet = showStrip && expanded;

  const sheetTouchY = useRef(null);
  const onSheetTouchStart = (e) => {
    setInteractionTick(t => t + 1);
    if (e.target.closest('.mps-blend')) return;
    sheetTouchY.current = e.touches[0].clientY;
  };
  const onSheetTouchEnd = (e) => {
    if (sheetTouchY.current === null) return;
    const delta = e.changedTouches[0].clientY - sheetTouchY.current;
    sheetTouchY.current = null;
    if (delta > SWIPE_DOWN_PX) setExpanded(false);
  };

  const topbar = (variant) => (
    <header className={`mps-topbar ${variant}`}>
      <button type="button" className="mps-icon-btn" onClick={onClose} aria-label={strings.back}>&lsaquo;</button>
      <button type="button" className="mps-title tappable" onClick={() => setSongsOpen(true)} title={strings.changeSong}>
        {title}
      </button>
      {partControls.length > 1 && !mePending && (
        <button type="button" className={`mps-badge ${hasMe ? '' : 'unset'}`} onClick={openMePart}>
          {hasMe ? mePartName : strings.pickMyPart}
        </button>
      )}
      <button type="button" className="mps-icon-btn" onClick={openMore} aria-label={strings.more}>&#8943;</button>
    </header>
  );

  const dock = (variant) => (
    <MobileDock
      variant={variant}
      blend={blend}
      blendPending={mePending}
      show={show}
      zoom={zoomControl}
      transport={transport}
      section={section}
      tempo={tempo}
      magnify={magnify}
    />
  );

  return (
    <div
      ref={shellRef}
      className={`mobile-practice-shell ${active ? 'active' : 'inactive'} ${inPractice ? 'practice' : 'setup'}`}
      aria-hidden={!active}
    >
      {dockInline && topbar('inline')}
      {topbarOverlay && topbar('overlay')}

      <div className={`mps-score ${lyrics.on && lyrics.available && lyrics.mode === 'magnify' && inPractice ? 'magnifying' : ''}`}>
        {children}
        {/* A looping section ignores jumps, like score taps */}
        <LyricsOverlay lyrics={lyrics} controls={controls} inPractice={inPractice} canSeek={!hasSection} />
      </div>

      {dockInline && dock('inline')}

      {showSheet && (
        <>
          <div className="mps-sheet-backdrop" onClick={() => setExpanded(false)} />
          <div className="mps-sheet" onTouchStart={onSheetTouchStart} onTouchEnd={onSheetTouchEnd}>
            <button
              type="button"
              className="mps-drawer-tab open"
              onClick={() => setExpanded(false)}
              aria-expanded={true}
              aria-label={strings.collapseControls}
            >
              <ChevronDownIcon size={22} />
            </button>
            {topbar('sheet')}
            {dock('sheet')}
          </div>
        </>
      )}

      {showStrip && (
        <MobileStrip
          transport={transport}
          blend={blend}
          blendPending={mePending}
          expanded={expanded}
          onExpand={() => setExpanded(true)}
          beat={beat}
        />
      )}

      {moreOpen && (
        <MobileMoreSheet
          controls={controls}
          locked={inPractice}
          partsOverride={partsOverride}
          onPartsOverride={setPartsOverride}
          stepHints={stepHints}
          onStepHints={setStepHints}
          stepHintsAvailable={hasMe}
          lyrics={lyrics}
          onClose={() => setMoreOpen(false)}
        />
      )}

      {/* Per-song settings auto-save. Its own buttons are hidden by design; it
          only needs to stay mounted to watch the player state and write the
          preset back (zoom, chosen part, blend, tempo, parts). */}
      <div hidden>
        <SettingsControls
          playerState={playerState}
          playerActions={playerActions}
          onStatusUpdate={setStatus}
          getCurrentFileContent={getCurrentFileContent}
          loadMusicXMLFromContent={loadMusicXMLFromContent}
          hasLoadedFile={hasLoadedFile}
          currentPresetName={currentPresetName}
          isLoading={isLoading}
        />
      </div>
      {songsOpen && (
        <MobileSongSheet
          locked={inPractice}
          onSelect={onSongSelect}
          onLocalFile={onLocalFileLoad}
          onRequireSignIn={onRequireSignIn}
          onClose={() => setSongsOpen(false)}
        />
      )}

      <SelectMePartDialog
        isOpen={meDialogVisible}
        parts={partControls}
        onSelect={selectMePart}
        onClose={closeMePart}
      />
    </div>
  );
};

export default MobilePracticeShell;
